import FeatureGates from '@atlaskit/feature-gate-js-client/feature-gates';
import type { NativeEmbedParameters } from '@atlaskit/native-embeds-common/types';
import { getParameters } from '@atlaskit/native-embeds-common/utils';
import { expValEquals } from '@atlaskit/tmp-editor-statsig/exp-val-equals';
import { expValEqualsNoExposure } from '@atlaskit/tmp-editor-statsig/exp-val-equals-no-exposure';
import { getNativeEmbedFacade } from '@atlassian/native-embeds-core/services/main';
import type { ExperienceManifest } from '@atlassian/native-embeds-core/types/experience/types';

const NATIVE_EMBED_EXPERIENCE_PREFIX = 'native-embed:';
const NATIVE_EMBED_MAX_WIDTH_CONFIG = 'platform_native_embeds_max_width';
// eslint-disable-next-line require-unicode-regexp
const DATABASE_NATIVE_EMBED_URL_PATTERN = /\/wiki\/spaces\/[^/]+\/database\/\d+(\?.*)?$/;

// Gates the vertical-resize behavior for db native embeds. While the experiment
// is off, the short-circuit for database embeds is skipped and
// `nativeEmbedAllowsVerticalResize` always reports `false`, so behavior is
// identical to the horizontal-only resize state. Pre-checks `cc-maui-experiment`
// (without firing exposure) so the v2 exposure population stays scoped to the
// MAUI cohort.
const isNativeDbEmbedsV2ExperimentEnabled = (): boolean =>
	expValEqualsNoExposure('cc-maui-experiment', 'isEnabled', true) &&
	expValEquals('databases-native-embeds-v2', 'isEnabled', true);

/**
 * Returns the experience id from a native-embed `extensionKey`, e.g.
 * `native-embed:database` → `database`. Handles keys where other segments
 * appear before the prefix (e.g. `…:native-embed:database`).
 */
export const getNativeEmbedExperienceIdFromExtensionKey = (
	extensionKey: string | undefined,
): string | undefined => {
	if (!extensionKey) {
		return undefined;
	}
	const idx = extensionKey.indexOf(NATIVE_EMBED_EXPERIENCE_PREFIX);
	if (idx === -1) {
		return undefined;
	}
	const id = extensionKey.slice(idx + NATIVE_EMBED_EXPERIENCE_PREFIX.length);
	return id || undefined;
};

type NativeEmbedNodeForManifest = {
	attrs: {
		extensionKey?: string;
		parameters?: NativeEmbedParameters & {
			url?: string | { value?: string };
		};
	};
};

const getNativeEmbedNodeUrl = (
	parameters: NativeEmbedNodeForManifest['attrs']['parameters'],
): string | undefined => {
	const urlFromMacroParams = getParameters(parameters, 'url');
	if (urlFromMacroParams) {
		return urlFromMacroParams;
	}

	const flatUrl = parameters?.url;
	if (typeof flatUrl === 'string') {
		return flatUrl;
	}
	return flatUrl?.value;
};

/**
 * Resolves the experience manifest. Prefers `extensionKey` (authoritative) over URL.
 */
export const resolveNativeEmbedManifestForNode = (
	node: NativeEmbedNodeForManifest,
): ExperienceManifest | undefined => {
	const facade = getNativeEmbedFacade();
	const experienceId = getNativeEmbedExperienceIdFromExtensionKey(node.attrs.extensionKey);
	if (experienceId) {
		const byId = facade.getManifestById(experienceId);
		if (byId) {
			return byId;
		}
	}
	const nodeUrl = getNativeEmbedNodeUrl(node.attrs.parameters);
	if (nodeUrl) {
		return facade.getManifestByUrl(nodeUrl);
	}
	return undefined;
};

export const isDatabaseNativeEmbedUrl = (url: string | undefined): boolean => {
	return !!url && DATABASE_NATIVE_EMBED_URL_PATTERN.test(url);
};

/**
 * When `true`, width/height stay proportional (no independent vertical resize).
 */
export const getNativeEmbedShouldLockAspectRatio = (node: NativeEmbedNodeForManifest): boolean => {
	if (isNativeDbEmbedsV2ExperimentEnabled()) {
		const id = getNativeEmbedExperienceIdFromExtensionKey(node.attrs.extensionKey);
		const nodeUrl = getNativeEmbedNodeUrl(node.attrs.parameters);
		if (id === 'database' || id === 'database-entry' || isDatabaseNativeEmbedUrl(nodeUrl)) {
			return false;
		}
	}

	const manifest = resolveNativeEmbedManifestForNode(node);
	if (manifest) {
		return manifest.lockResizeAspectRatio?.() ?? true;
	}
	return true;
};

export const nativeEmbedAllowsVerticalResize = (node: NativeEmbedNodeForManifest): boolean => {
	if (!isNativeDbEmbedsV2ExperimentEnabled()) {
		return false;
	}
	const manifest = resolveNativeEmbedManifestForNode(node);
	return manifest?.allowVerticalResize ?? false;
};

/**
 * Whether this embed may use the editor's viewport-constrained max layout width.
 * Both the experience opt-in and the dynamic configuration must be enabled.
 */
export const nativeEmbedAllowsMaxWidth = (node: NativeEmbedNodeForManifest): boolean => {
	const manifest = resolveNativeEmbedManifestForNode(node);
	return (
		manifest?.allowMaxWidth === true &&
		FeatureGates.getExperimentValue<boolean>(NATIVE_EMBED_MAX_WIDTH_CONFIG, 'value', false)
	);
};
